import DataSetsTable from 'js/components/data_sets/DataSetsTable';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import { CONTACT_SUPPORT_LINK } from 'js/constants/config';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { SUPPORT_KEY_DATASET_LIST } from 'js/constants/supportPageKeys';
import {
  AvailableFilterTypesByView,
  SearchFilterViews,
  SearchFilterViewsType,
} from 'js/constants/visibilityFilters';
import { applyFilters, processClientSideSearchFilters } from 'js/lib/SearchFilterUtils';
import { isEmpty, pick } from 'lodash';
import { BannerDescription } from 'new/elements/BannerDescription';
import { ExclusiveButtons } from 'new/elements/ExclusiveButtons';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ResourceEditProvider from 'js/components/ResourceEditProvider';

import './ViewDataSets.scss';

const description =
  'Nexla datasets offer a virtual model of data, comprising schema, samples, and inferred metadata.';

const dataSetAccessControls = [
  {
    label: 'All Nexsets',
    value: 'all',
  },
  {
    label: 'Data Shared with Me',
    value: 'shared',
  },
];

const ViewDataSets = ({
  dataSets,
  availableDatasets,
  resourceId,
  filters,
  entities,
  dataSetSchemas,
  availableFilters,
  isLoading,
  getSourceMetaFromDataSet,
  loadDataSet,
  loadDataFeedDataSets,
  loadMyAvailableDataSets,
  setContextualSupport,
  unsetContextualSupport,
}) => {
  const [dataSetAccess, setDataSetAccess] = useState('all');
  const filteredAvailableDataSets = useMemo(() => {
    const { filteredObjects } = applyFilters(
      availableDatasets,
      processClientSideSearchFilters(
        filters[SearchFilterViews.AVAILABLE_DATA_SETS],
        pick(availableFilters, AvailableFilterTypesByView[SearchFilterViews.AVAILABLE_DATA_SETS]),
      ),
      entities,
      dataSetSchemas,
    );

    return filteredObjects;
  }, [availableDatasets, filters]);
  const showAvailableDataSets = dataSetAccess === 'shared';

  useEffect(() => {
    setContextualSupport(SUPPORT_KEY_DATASET_LIST);

    return () => {
      unsetContextualSupport(SUPPORT_KEY_DATASET_LIST);
    };
  }, []);

  useEffect(() => {
    if (resourceId) {
      loadDataSet(resourceId);
    }
  }, [resourceId]);

  const onDataSetAccessChange = (_, value) => {
    setDataSetAccess(value);

    if (value === 'shared') {
      loadMyAvailableDataSets();
    }
  };

  let view: SearchFilterViewsType;
  let filtersTypes: string[];
  let data: any;
  let isClientSideSearch = false;
  let loadData: () => void;

  if (showAvailableDataSets) {
    view = SearchFilterViews.AVAILABLE_DATA_SETS;
    data = filteredAvailableDataSets;
    isClientSideSearch = true;
    loadData = () => {};
  } else {
    view = SearchFilterViews.MY_DATA_SETS;
    data = dataSets;
    loadData = loadDataFeedDataSets;
  }

  filtersTypes = AvailableFilterTypesByView[view];

  let noContentView = null;

  if (!isLoading && isEmpty(data)) {
    if (resourceId) {
      noContentView = (
        <NoContentTextView
          title={<div>We are sorry, we could not find this Nexset.</div>}
          subtitle={
            <div>
              Please reload the page or file a{' '}
              <a style={{ color: '#507fe7' }} href={CONTACT_SUPPORT_LINK}>
                support ticket
              </a>{' '}
              to request support.
            </div>
          }
        />
      );
    } else {
      noContentView = (
        <NoContentTextView
          title={
            <div>
              We have not detected any Nexsets yet. Create a source, or if you just setup your first
              source please wait a few minutes for the source to be scanned
            </div>
          }
          subtitle={
            <div>
              Click{' '}
              <Link to={`${ROUTE_CONNECT}/flows/select-type`} style={{ color: '#507fe7' }}>
                here
              </Link>{' '}
              to setup a Flow and Nexla will automatically detect Nexsets
            </div>
          }
        />
      );
    }
  }

  return (
    <ResourceEditProvider>
      <div>
        <BannerDescription title="Nexsets" description={description} />
        <div className="data-set-access-switch-container">
          <ExclusiveButtons
            data={dataSetAccessControls}
            value={dataSetAccess}
            onChange={onDataSetAccessChange}
          />
        </div>
        <DataSetsTable
          key={view}
          view={view}
          urlId={resourceId}
          isLoading={isLoading}
          isClientSideSearch={isClientSideSearch}
          data={data}
          getSourceMeta={getSourceMetaFromDataSet}
          availableFilterTypes={filtersTypes}
          loadData={loadData}
          noContentView={noContentView}
        />
      </div>
    </ResourceEditProvider>
  );
};

export default ViewDataSets;
